import { useMemo } from 'react'
import { Switch, Route, Redirect, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import TabHeader from './components/TabHeader/TabHeader'
import ExecutiveFilterBar from './components/FilterBar/ExecutiveFilterBar'
import DrillDrawer from './components/DrillDrawer/DrillDrawer'
import DrillOutlet from './components/DrillOutlet/DrillOutlet'
import ExecutiveTabsNav from './components/ExecutiveTabsNav/ExecutiveTabsNav'
import ExecutivePrintView from './export/ExecutivePrintView'
import { Box } from '@mui/material'
import { LayoutRoot } from './usedStyle'
import { routeName } from 'routes/routes-name'
import { useExecutivePermissions } from './permission-store'
import { getExecutiveEntryPath, isExecutiveTabAllowed, resolveExecutiveTab } from './executive-nav.utils'
import type { ExecutiveState } from 'app/slice/executive.slice'
import 'styles/ExecutivePrint.css'

import ExecutiveIndex from './ExecutiveIndex'
import OverviewTab from './overview/OverviewTab'
import PerformanceTab from './performance/PerformanceTab'
import AccountTab from './account/AccountTab'
import SalesTab from './sales/SalesTab'
import InsightTab from './insight/InsightTab'

export default function ExecutiveLayout() {
  const { t } = useTranslation()
  const location = useLocation()
  const activeTab = resolveExecutiveTab(location.pathname)
  const perms = useExecutivePermissions()
  const filters = useSelector((s: { executive: ExecutiveState }) => s.executive.filters)
  // useMemo MUST be called before any conditional early return below to satisfy
  // rules-of-hooks (call order must be identical on every render).
  const now = useMemo(() => new Date(), [])

  // A denied tab falls back to the overview (or the legacy dashboard when the
  // user has no executive tab at all) — see getExecutiveEntryPath.
  if (!isExecutiveTabAllowed(activeTab, perms.executiveDashboardTabs)) {
    return <Redirect to={getExecutiveEntryPath(perms.executiveDashboardTabs)} />
  }

  const userScope = perms.executiveDashboardScope
  const userBranchIds: number[] = []

  const activeTabLabel = t(`EXECUTIVE.TAB.${activeTab.toUpperCase()}`)
  const branchLabel = filters.branchId != null ? String(filters.branchId) : '—'
  const dateRangeLabel = filters.dateRange.preset

  return (
    <LayoutRoot className="executive-print-root">
      <ExecutivePrintView
        activeTab={activeTab}
        activeTabLabel={activeTabLabel}
        branchLabel={branchLabel}
        dateRangeLabel={dateRangeLabel}
        now={now}
      />
      {/* Sticky scope: grows with content so the sticky bar stays attached
          through the entire scroll. LayoutRoot has height:100% which would
          otherwise cap the sticky's containing block at viewport height. */}
      <Box>
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: (theme) => theme.zIndex.appBar,
            backgroundColor: 'background.paper',
          }}
        >
          <TabHeader activeTab={activeTabLabel} />
          <ExecutiveTabsNav />
        </Box>
        <ExecutiveFilterBar tab={activeTab} userScope={userScope} userBranchIds={userBranchIds} />
        <Switch>
          <Route exact path={routeName.executiveOverview} component={OverviewTab} />
          <Route exact path={routeName.executivePerformance} component={PerformanceTab} />
          <Route exact path={routeName.executiveAccount} component={AccountTab} />
          <Route exact path={routeName.executiveSales} component={SalesTab} />
          <Route exact path={routeName.executiveInsight} component={InsightTab} />
          <Route exact path={routeName.executive} component={ExecutiveIndex} />
        </Switch>
      </Box>
      <DrillDrawer>
        <DrillOutlet />
      </DrillDrawer>
    </LayoutRoot>
  )
}
